import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import './dotglobal-org-tile.js';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TOrgSort = 'Repos' | 'Stars' | 'Name';

/**
 * Organizations directory: a banner with totals, a search field and a Repos/Stars/Name
 * sort control over the full set of org tiles. Reached from the Explore "See all
 * organizations" button (route `#/orgs`). Mirrors the Claude Design AllOrgs view.
 */
@customElement('dotglobal-all-orgs')
export class DotglobalAllOrgs extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicOverview['response'] | null = null;

  @state()
  private accessor query = '';

  @state()
  private accessor sort: TOrgSort = 'Repos';

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; flex: 1; }
      .empty { padding: 60px 20px; text-align: center; color: var(--text-muted); }

      .orgs-banner { border-bottom: 1px solid var(--border-default); background: var(--bg-secondary); }
      .backlink {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 12px;
        font-size: 12.5px;
        font-weight: 500;
        color: var(--text-secondary);
        cursor: pointer;
      }
      .backlink:hover { color: var(--text-primary); }
      .banner-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
      .banner-row h1 {
        margin: 0;
        font-family: var(--font-display);
        font-size: 30px;
        font-weight: 800;
        letter-spacing: -0.02em;
      }
      .banner-row p { margin: 8px 0 0; max-width: 560px; font-size: 14px; line-height: 1.55; color: var(--text-secondary); }
      .banner-stats { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; font-size: 12.5px; }
      .banner-stats .meta { display: inline-flex; align-items: center; gap: 6px; color: var(--text-muted); }
      .banner-stats b { color: var(--text-primary); }

      .toolbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin-bottom: 18px; }

      .navsearch { position: relative; display: flex; align-items: center; flex: 1; max-width: 320px; min-width: 200px; }
      .navsearch dees-icon { position: absolute; left: 11px; color: var(--text-muted); pointer-events: none; }
      .navsearch input {
        width: 100%;
        height: 34px;
        padding: 0 12px 0 34px;
        background: var(--bg-tertiary);
        border: 1px solid var(--border-default);
        border-radius: 8px;
        color: var(--text-primary);
        font-size: 13px;
        outline: none;
        transition: border-color 0.15s, box-shadow 0.15s;
      }
      .navsearch input::placeholder { color: var(--text-muted); }
      .navsearch input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent); }

      .seg { display: inline-flex; padding: 2px; border: 1px solid var(--border-default); border-radius: 8px; background: var(--bg-tertiary); }
      .seg button {
        height: 26px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0 11px;
        border-radius: 6px;
        color: var(--text-secondary);
        font-size: 12px;
        font-weight: 500;
      }
      .seg button:hover { color: var(--text-primary); }
      .seg button.on { color: var(--text-primary); background: var(--bg-primary); box-shadow: var(--shadow-xs); }

      .orgs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); gap: 14px; }
    `,
  ];

  public render(): TemplateResult {
    const data = this.data;
    if (!data) {
      return html`<div class="page"><div class="empty">No organizations loaded yet.</div></div>`;
    }
    const orgs = data.orgs;
    const totalRepos = orgs.reduce((sum, org) => sum + (org.repoCount || 0), 0);
    const totalStars = orgs.reduce((sum, org) => sum + (org.starsCount || 0), 0);
    const query = this.query.trim().toLowerCase();
    const rows = orgs
      .filter((org) => !query || `${org.name} ${org.description ?? ''}`.toLowerCase().includes(query))
      .sort((orgA, orgB) => {
        if (this.sort === 'Stars') {
          return orgB.starsCount - orgA.starsCount;
        }
        if (this.sort === 'Name') {
          return orgA.name.localeCompare(orgB.name);
        }
        return orgB.repoCount - orgA.repoCount;
      });

    return html`
      <div class="stack" style="flex: 1">
        <div class="orgs-banner">
          <div class="page" style="padding-top: 26px; padding-bottom: 26px">
            <button class="backlink" @click=${() => this.navigate('#/')}>${this.icon('ChevronLeft')} Back to explore</button>
            <div class="banner-row">
              <div>
                <h1>Organizations</h1>
                <p>Every brand building in the open on foss.global.</p>
              </div>
              <div class="banner-stats">
                <span class="meta">${this.icon('Building')} <b>${orgs.length}</b> orgs</span>
                <span class="meta">${this.icon('BookOpen')} <b>${totalRepos}</b> repos</span>
                <span class="meta">${this.icon('Star')} <b>${totalStars}</b> stars</span>
              </div>
            </div>
          </div>
        </div>
        <div class="page content">
          <div class="toolbar">
            <div class="navsearch">
              ${this.icon('Search')}
              <input
                .value=${this.query}
                placeholder="Find an organization..."
                @input=${(eventArg: Event) => { this.query = (eventArg.target as HTMLInputElement).value; }}
              />
            </div>
            <div class="seg">
              ${(['Repos', 'Stars', 'Name'] as TOrgSort[]).map((option) => html`
                <button class=${this.sort === option ? 'on' : ''} @click=${() => { this.sort = option; }}>
                  ${this.icon(option === 'Stars' ? 'Star' : option === 'Name' ? 'ArrowDownAZ' : 'BookOpen')} ${option}
                </button>
              `)}
            </div>
          </div>
          ${rows.length
            ? html`<div class="orgs-grid">${rows.map((org) => html`<dotglobal-org-tile .org=${org}></dotglobal-org-tile>`)}</div>`
            : html`<div class="empty">No organizations match "${this.query}".</div>`}
        </div>
      </div>
    `;
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${14}></dees-icon>`;
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-all-orgs': DotglobalAllOrgs;
  }
}
